import { useMemo, useState, type ReactNode } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Columns3,
  Download,
  Search,
  X,
} from 'lucide-react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { Card } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Popover } from '@/components/ui/Popover';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { useUserPrefs } from '@/api/bi/settings';
import { cn } from '@/lib/cn';

export interface DataViewProps<T> {
  /** Rows for the current page. The component is intentionally page-aware: server pagination keeps us from over-fetching. */
  data: T[];
  columns: ColumnDef<T, unknown>[];
  /** Title shown in the header strip — keeps the section self-describing without requiring a wrapping <Card>. */
  title?: string;
  description?: string;
  /** Optional toolbar slot (filter chips, status pills, dropdowns). Renders right-aligned next to the search box. */
  toolbar?: ReactNode;
  /** Search behaviour. If `onSearchChange` is supplied, the input is controlled and the parent owns debouncing/wiring. */
  search?: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
  };
  /** Pagination — `total` enables the "showing X-Y of Z" footer. Pass `undefined` for infinite/cursor lists. */
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (next: number) => void;
    onPageSizeChange?: (next: number) => void;
    pageSizeOptions?: number[];
  };
  /** CSV export hook. Receives the current visible page; for a full export, do it in the parent. */
  onExportCsv?: () => void;
  /** Per-row click handler (drilldown). The whole row becomes a button. */
  onRowClick?: (row: T) => void;
  /** Client-side pagination: sort the full `data` array first, then page it locally. */
  clientPagination?: boolean;
  /** Loading & error wires from React Query — the component decides which empty / loading / error UI to render. */
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** Empty-state copy — playful but useful. */
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  /** Optional footer content (e.g. totals row). */
  footer?: ReactNode;
  className?: string;
  /**
   * Density. `compact` shaves vertical rhythm for power-user views. Omit to
   * follow the viewer's Appearance preference, which is the right default —
   * someone who asked for compact rows meant everywhere, not one page.
   */
  density?: 'comfortable' | 'compact';
  /**
   * Cap the table's height so it scrolls internally, which is what makes the
   * header stick.
   *
   * Without a bounded scroll container `position: sticky` has nothing to stick
   * inside: the wrapper's `overflow-x: auto` already forces `overflow-y: auto`,
   * but its height grows with the content so it never actually scrolls and the
   * page scrolls instead. Pass e.g. `'70vh'` on long lists.
   */
  maxHeight?: string;
  /**
   * Pin the first column while scrolling horizontally. Worth it on wide tables
   * where the identifying column is the one you lose first.
   */
  stickyFirstColumn?: boolean;
  /**
   * Let the viewer hide columns. Needs `id` (or an accessor key) on each column
   * so there is something to label the toggles with.
   */
  columnToggles?: boolean;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100];

/**
 * Generic, opinionated table.
 *
 * Why this exists:
 *   - Every list page in BI shares the same skeleton: header → toolbar →
 *     table → pagination. Centralising it means consistent behaviour,
 *     keyboard support, and zero copy-pasta.
 *   - Built on @tanstack/react-table for sorting + column rendering, but
 *     pagination is server-driven (we don't trust the client to slice).
 *   - All loading, error and empty states are first-class, so callers
 *     never have to wire them themselves.
 *
 * Animation notes (Emil): row hover is color-only (no movement); the active
 * row uses `.press` for the standard scale-on-press feedback. The pagination
 * pill rail uses transitions, not keyframes, so rapid clicks stay smooth.
 */
export function DataView<T>({
  data,
  columns,
  title,
  description,
  toolbar,
  search,
  pagination,
  onExportCsv,
  onRowClick,
  clientPagination = false,
  isLoading,
  isError,
  onRetry,
  emptyTitle = 'Nothing here',
  emptyDescription,
  footer,
  className,
  density,
  maxHeight,
  stickyFirstColumn = false,
  columnToggles = false,
}: DataViewProps<T>) {
  const prefs = useUserPrefs();
  const rowDensity = density ?? prefs.density ?? 'comfortable';

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const paginationState = useMemo<PaginationState | undefined>(
    () =>
      pagination
        ? { pageIndex: Math.max(0, pagination.page - 1), pageSize: pagination.pageSize }
        : undefined,
    [pagination],
  );
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    if (clientPagination && pagination && pagination.page !== 1) {
      pagination.onPageChange(1);
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      ...(clientPagination && paginationState ? { pagination: paginationState } : {}),
    },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(clientPagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    manualPagination: !clientPagination,
  });

  const showHeader = Boolean(title || description || toolbar || search || onExportCsv || columnToggles);
  const hiddenCount = table.getAllLeafColumns().filter((column) => !column.getIsVisible()).length;

  const totalPages = useMemo(() => {
    if (!pagination) return null;
    return Math.max(1, Math.ceil(pagination.total / Math.max(1, pagination.pageSize)));
  }, [pagination]);

  const showFrom = pagination ? (pagination.page - 1) * pagination.pageSize + 1 : null;
  const showTo = pagination ? Math.min(pagination.total, pagination.page * pagination.pageSize) : null;

  return (
    <Card className={cn('overflow-hidden', className)}>
      {showHeader ? (
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            {title ? <h3 className="text-sm font-semibold tracking-tight">{title}</h3> : null}
            {description ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            {search ? (
              <div className="relative min-w-0 sm:w-72">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                  value={search.value}
                  onChange={(e) => search.onChange(e.target.value)}
                  placeholder={search.placeholder ?? 'Search…'}
                  className="h-8 pl-7 pr-7 text-xs"
                />
                {search.value ? (
                  <button
                    type="button"
                    onClick={() => search.onChange('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                ) : null}
              </div>
            ) : null}
            {toolbar}
            {columnToggles ? (
              <ColumnToggles
                columns={table.getAllLeafColumns().map((column) => ({
                  id: column.id,
                  label: columnLabel(column.id, column.columnDef.header),
                  visible: column.getIsVisible(),
                  canHide: column.getCanHide(),
                  toggle: column.getToggleVisibilityHandler(),
                }))}
                hiddenCount={hiddenCount}
                onShowAll={() => setColumnVisibility({})}
              />
            ) : null}
            {onExportCsv ? (
              <Button variant="outline" size="sm" onClick={onExportCsv}>
                <Download className="h-3.5 w-3.5" aria-hidden />
                CSV
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div
        className={cn(maxHeight ? 'overflow-auto' : 'overflow-x-auto')}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {isError ? (
          <div className="px-5 py-6">
            <ErrorState onRetry={onRetry} />
          </div>
        ) : isLoading && data.length === 0 ? (
          <DataSkeleton rows={pagination?.pageSize ?? 8} columns={columns.length} density={rowDensity} />
        ) : data.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title={emptyTitle} description={emptyDescription} />
          </div>
        ) : (
          <table
            className={cn(
              'w-full text-left text-sm font-cond',
              // Sticky cells need their own background and borders, and
              // `border-collapse` drops the borders of sticky cells as they
              // scroll. `border-separate` keeps them.
              (maxHeight || stickyFirstColumn) && 'border-separate border-spacing-0',
            )}
          >
            <thead className={cn(!maxHeight && 'border-b border-border bg-muted/40')}>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header, index) => {
                    const sortDir = header.column.getIsSorted();
                    const sortable = header.column.getCanSort();
                    const first = index === 0;
                    return (
                      <th
                        key={header.id}
                        scope="col"
                        aria-sort={
                          sortable
                            ? sortDir === 'asc'
                              ? 'ascending'
                              : sortDir === 'desc'
                                ? 'descending'
                                : 'none'
                            : undefined
                        }
                        className={cn(
                          'whitespace-nowrap px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground',
                          maxHeight && 'sticky top-0 z-20 border-b border-border bg-muted',
                          !maxHeight && 'border-b border-border bg-muted/40',
                          stickyFirstColumn && first && 'sticky left-0 z-30 bg-muted',
                        )}
                      >
                        {sortable ? (
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className="inline-flex items-center gap-1 rounded uppercase tracking-wider focus-ring"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                            {sortDir === 'asc' ? (
                              <ChevronDown className="h-3 w-3 rotate-180" aria-hidden />
                            ) : sortDir === 'desc' ? (
                              <ChevronDown className="h-3 w-3" aria-hidden />
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden />
                            )}
                          </button>
                        ) : header.isPlaceholder ? null : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className={cn(!(maxHeight || stickyFirstColumn) && 'divide-y divide-border')}>
              {table.getRowModel().rows.map((row) => {
                const interactive = Boolean(onRowClick);
                return (
                  <tr
                    key={row.id}
                    onClick={interactive ? () => onRowClick?.(row.original) : undefined}
                    className={cn(
                      'group transition-colors duration-100 ease-out',
                      interactive && 'press cursor-pointer hover:bg-muted/40 focus-ring',
                    )}
                    tabIndex={interactive ? 0 : undefined}
                    onKeyDown={
                      interactive
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowClick?.(row.original);
                            }
                          }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <td
                        key={cell.id}
                        className={cn(
                          'whitespace-nowrap px-4 align-middle text-foreground',
                          rowDensity === 'compact' ? 'py-1.5 text-[12.5px]' : 'py-2.5 text-sm',
                          (maxHeight || stickyFirstColumn) && 'border-b border-border',
                          // An opaque background is mandatory on a sticky cell:
                          // a translucent one lets the scrolling columns show
                          // through it.
                          stickyFirstColumn &&
                            index === 0 &&
                            'sticky left-0 z-10 bg-surface group-hover:bg-muted',
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
            {footer ? <tfoot className="border-t border-border bg-muted/30">{footer}</tfoot> : null}
          </table>
        )}
      </div>

      {pagination && data.length > 0 ? (
        <div className="flex flex-col gap-3 border-t border-border px-5 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Badge tone="neutral" className="!px-2">
              {showFrom}–{showTo} of {pagination.total.toLocaleString()}
            </Badge>
            {isLoading ? <span className="text-[10px] uppercase tracking-wider">refreshing…</span> : null}
            {pagination.onPageSizeChange ? (
              <select
                value={pagination.pageSize}
                onChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
                className="press h-7 rounded-md border border-input bg-surface px-2 text-xs focus-ring"
                aria-label="Rows per page"
              >
                {(pagination.pageSizeOptions ?? DEFAULT_PAGE_SIZES).map((size) => (
                  <option key={size} value={size}>
                    {size}/page
                  </option>
                ))}
              </select>
            ) : null}
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange(Math.max(1, pagination.page - 1))}
              disabled={pagination.page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <span className="px-1 tabular-nums">
              {pagination.page} <span className="text-muted-foreground/60">/</span> {totalPages ?? 1}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange(Math.min(totalPages ?? 1, pagination.page + 1))}
              disabled={pagination.page >= (totalPages ?? 1)}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

interface ToggleableColumn {
  id: string;
  label: string;
  visible: boolean;
  canHide: boolean;
  toggle: (event: unknown) => void;
}

/**
 * Show/hide columns. Wide operational tables carry columns that only matter to
 * one team, and the alternative to hiding them is horizontal scrolling past
 * them on every visit.
 */
function ColumnToggles({
  columns,
  hiddenCount,
  onShowAll,
}: {
  columns: ToggleableColumn[];
  hiddenCount: number;
  onShowAll: () => void;
}) {
  const hideable = columns.filter((column) => column.canHide);
  if (hideable.length === 0) return null;

  return (
    <Popover
      align="end"
      className="w-56 p-2"
      trigger={
        <Button variant="outline" size="sm">
          <Columns3 className="h-3.5 w-3.5" aria-hidden />
          Columns
          {hiddenCount > 0 ? (
            <Badge tone="neutral" className="!px-1.5 !py-0 text-[10px]">
              {hiddenCount} hidden
            </Badge>
          ) : null}
        </Button>
      }
    >
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Visible columns
        </span>
        {hiddenCount > 0 ? (
          <button
            type="button"
            onClick={onShowAll}
            className="rounded text-[10px] text-primary hover:underline focus-ring"
          >
            Show all
          </button>
        ) : null}
      </div>
      <ul className="max-h-72 space-y-0.5 overflow-auto">
        {hideable.map((column) => (
          <li key={column.id}>
            <div className="rounded px-1 py-1 hover:bg-muted">
              <Checkbox
                label={column.label}
                checked={column.visible}
                onChange={column.toggle}
                className="w-full"
              />
            </div>
          </li>
        ))}
      </ul>
    </Popover>
  );
}

/**
 * A human name for a column toggle. Header definitions are often render
 * functions rather than strings, in which case the column id is all we have —
 * `created_at` reads better as "Created at".
 */
function columnLabel(id: string, header: unknown): string {
  if (typeof header === 'string' && header.trim()) return header;
  const cleaned = id.replace(/[_.-]+/g, ' ').trim();
  if (!cleaned) return id;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function DataSkeleton({
  rows,
  columns,
  density,
}: {
  rows: number;
  columns: number;
  density: 'comfortable' | 'compact';
}) {
  return (
    <div className="px-5 py-4">
      <div className="space-y-2">
        {Array.from({ length: Math.min(rows, 10) }).map((_, r) => (
          <div key={r} className="flex items-center gap-2">
            {Array.from({ length: columns }).map((__, c) => (
              <Skeleton
                key={c}
                className={cn(
                  'flex-1',
                  density === 'compact' ? 'h-5' : 'h-7',
                  c === 0 && 'max-w-[15%]',
                  c === columns - 1 && 'max-w-[12%]',
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Tiny CSV exporter — handles quoting + the BOM Excel needs to detect UTF-8.
 *
 * Pass `headers` to control column order/labels; the function looks each
 * header up by string key on each row. Numbers, booleans and null are
 * stringified safely.
 */
export function downloadCsv<T extends Record<string, unknown>>(
  filename: string,
  rows: T[],
  headers: { key: keyof T & string; label?: string }[],
): void {
  const head = headers.map((h) => csvCell(h.label ?? h.key)).join(',');
  const body = rows
    .map((r) =>
      headers
        .map((h) => {
          const v = r[h.key];
          if (v == null) return '';
          if (typeof v === 'object') return csvCell(JSON.stringify(v));
          return csvCell(String(v));
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob(['\uFEFF', head, '\n', body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function csvCell(s: string): string {
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
